import { useQuery } from '@tanstack/react-query'
import { getAvailableToConfigure } from '@/api/settings-api'
import { PbConfig, PbParam } from '@itsrever/rever-api'
import { Country, EU_ALIAS_CODE, EU_ALIAS_NAME } from '@/domain/countries'
import { useEcommerceId } from './use-ecommerce-id'
import { usePortalTranslate } from './use-portal-translate'

export type ShippingConfiguration = {
  name: string // Shouldn't be in frontend
  params: PbParam[] // Shouldn't be in frontend
  functions: ShippingFunction[]
}

export type ShippingFunction = {
  function: string
  comparison: string[]
  values: string[]
  variable: string // Shouldn't be in frontend
}

const NO_CONFIGURATION: ShippingConfiguration = {
  name: '',
  params: [],
  functions: []
}
const NO_REASON_KEYS: string[] = []

export function useAvailableShippingRules(countries: Country[]) {
  const { trans } = usePortalTranslate({})
  const ecommerceId = useEcommerceId()
  const enabled = !!ecommerceId && countries.length > 0

  const query = useQuery({
    queryKey: ['availableShippingRules', ecommerceId],
    queryFn: async () => {
      const response = await getAvailableToConfigure(ecommerceId ?? '')
      const configurations = response.configurations ?? []
      return {
        availableConfiguration: toFrontendFunctions(
          configurations,
          countries,
          trans
        ),
        reasonKeys: findReasonKeys(configurations)
      }
    },
    enabled
  })

  return {
    availableConfiguration:
      query.data?.availableConfiguration ?? NO_CONFIGURATION,
    reasonKeys: query.data?.reasonKeys ?? NO_REASON_KEYS,
    loadingAvailableRules: query.isLoading,
    // Saving without it would write every rule without its name and variables.
    availableRulesFailed: query.isError,
    reloadAvailableRules: () => {
      if (enabled) query.refetch()
    }
  }
}

export function findReasonKeys(config: PbConfig[]): string[] {
  if (config.length === 0) return []
  const keys =
    config[0].functions
      ?.filter((f) => f.name === 'returnReason')
      .map((f) => f.values ?? []) ?? []
  return keys.flat()
}

export function toFrontendFunctions(
  config: PbConfig[],
  countries: Country[],
  trans: (key: string) => string
): ShippingConfiguration {
  if (!config || config.length === 0)
    return { name: '', params: [], functions: [] }

  const frontendFunctions: ShippingFunction[] =
    config[0].functions?.map((func) => {
      const frontendFunc: ShippingFunction = {
        function: func.name ?? '',
        comparison: func.comparisons?.map((comp) => comp) ?? [],
        values: toFrontendValues(
          func.name ?? '',
          func.values ?? [],
          countries,
          trans
        ),
        variable: func.variable ?? ''
      }
      return frontendFunc
    }) ?? []

  const configuration: ShippingConfiguration = {
    name: config[0].name ?? '',
    params: config[0].params ?? [],
    functions: frontendFunctions
  }
  return configuration
}

export function toFrontendFunction(func: string): string {
  switch (func) {
    case 'country':
      return 'Country'
    case 'refundMethod':
      return 'Refund method'
    case 'returnReason':
      return 'Return reason'
    case 'returnMethod':
      return 'Return method'
    case 'postalCodePrefix':
      return 'Postal code'
    case 'reverExchange':
      return 'Order comes from an exchange'
    case 'orderFullyReturned':
      return 'Order fully returned'
    case 'loyalCustomer':
      return 'Customer is loyal'
    case 'productCategoryID':
      return 'Product Category ID'
    case 'productCollectionID':
      return 'Product Collection ID'
    case 'productTagID':
      return 'Product Tag ID'
    case 'processProductID':
      return 'Process Product ID'
    case 'orderTotalAmount':
      return 'Total amount'
    case 'orderTotalNumberOfProducts':
      return 'Total number of products'
    case 'totalProductsReturned':
      return 'Number of products returned'
    case 'totalProductsValue':
      return 'Total products value'
    case 'committedExchangeAmount':
      return 'Refunded exchange amount'
    case 'committedPromocodeAmount':
      return 'Refunded promocode amount'
    case 'committedGiftAmount':
      return 'Refunded gift card amount'
    case 'committedBankTransferAmount':
      return 'Refunded bank transfer amount'
    case 'committedOriginalAmount':
      return 'Refunded original amount'
    case 'daysSinceBought':
      return 'Days since bought'
    case 'orderPurchased':
      return 'Order purchased'
    case 'orderTagID':
      return 'Order Tag ID'
    default:
      return func
  }
}

export function toFrontendComparison(comparison: string): string {
  switch (comparison) {
    case 'In':
      return 'In'
    case 'NotIn':
      return 'Not In'
    case 'Is':
      return 'Is'
    case 'NotIs':
      return 'Is not'
    case 'GreaterThan':
      return '>'
    case 'GreaterThanOrEqual':
      return '≥'
    case 'LessThan':
      return '<'
    case 'LessThanOrEqual':
      return '≤'
    case 'Equal':
      return '='
    case 'NotEqual':
      return '≠'
    default:
      return comparison
  }
}

export function toFrontendValues(
  func: string,
  values: string[],
  countries: Country[],
  trans: (key: string) => string
): string[] {
  const funcName = toFrontendFunction(func)
  switch (funcName) {
    case 'Country':
      return countriesToDisplay(countries, values)
    case 'Refund method':
      return values.map((val) => toReadableRefundMethod(val))
    case 'Return reason':
      return values.map((val) => toReadableReturnReason(val, trans))
    case 'Return method':
      return values.map((val) => toReadableReturnMethod(val))
    case 'Order comes from an exchange':
      return ['True']
    case 'Order fully returned':
      return ['True']
    case 'Customer is loyal':
      return ['True']

    case 'Total amount':
    case 'Total products value':
    case 'Refunded exchange amount':
    case 'Refunded promocode amount':
    case 'Refunded gift card amount':
    case 'Refunded bank transfer amount':
    case 'Refunded original amount':
      return toIntMoney(values)
    default:
      return values
  }
}

function toIntMoney(values: string[]): string[] {
  if (values.length === 0) {
    return ['0']
  }
  return values.map((v) => (parseFloat(v ?? '0') / 100).toString())
}

function countriesToDisplay(countries: Country[], values: string[]): string[] {
  return values.map((val) => countryCodeToCountryName(val, countries))
}

function countryCodeToCountryName(code: string, countries: Country[]): string {
  if (code === EU_ALIAS_CODE) return EU_ALIAS_NAME
  const country = countries.find((country) => country.iso === code)
  return country?.name ?? code
}

function toReadableRefundMethod(method: string): string {
  switch (method) {
    case 'BANK_TRANSFER':
      return 'Bank transfer'
    case 'ORIGINAL':
      return 'Original payment method'
    case 'PROMO_CODE':
      return 'Promo code'
    case 'GIFT_CARD':
      return 'Gift card'
    case 'EXCHANGE':
      return 'Exchange'
    default:
      return method
  }
}

export function toReadableReturnReason(
  reason: string,
  trans: (key: string) => string
): string {
  if (reason === 'NO_RETURN_REASON') return 'No reason'
  return trans(`select_reason_${reason}`)
}

function toReadableReturnMethod(method: string): string {
  switch (method) {
    case 'HOME_PICK_UP':
      return 'Home pick-up'
    case 'DROP_OFF_POINT':
      return 'Drop-off point'
    case 'DELIVERY_PICKUP':
      return 'Delivery and pick-up'
    case 'IN_STORE':
      return 'In store'
    case 'SELF_SHIPPING':
      return 'Self shipping'
    default:
      return method
  }
}
